import { create } from "zustand";

/**
 * Owns the mandatory-form -> review sidesheet chain for a single contractor.
 * Mounted globally via EnquiryFlowShell (see providers.js) so any
 * "Get in touch" trigger anywhere can call openMandatory() without prop
 * drilling. The survey step itself is intentionally NOT owned here — see
 * surveySheetStore.js, which is fully standalone and reusable on its own;
 * EnquiryFlowShell hosts it inline (tagged via context: "enquiryFlow") when
 * it's part of this chain, sliding between it and this store's own steps.
 */
export const useEnquiryFlowStore = create((set) => ({
  activeStep: null, // null | 'mandatory' | 'review' | 'success'
  digitalProfile: null,
  mandatoryData: null,
  surveyAnswers: {},
  // True only when the mandatory sheet was reopened via the Review sheet's
  // edit pencil — distinguishes "back should return to Review unchanged"
  // from "back should abandon the enquiry" (fresh start, or bounced back
  // one step from the survey with nowhere earlier to go).
  isEditingFromReview: false,

  openMandatory: (digitalProfile) =>
    set({
      activeStep: "mandatory",
      digitalProfile,
      mandatoryData: null,
      surveyAnswers: {},
      isEditingFromReview: false,
    }),

  // Returning homeowner with saved details: skip straight to Review.
  openReview: ({ digitalProfile, mandatoryData, surveyAnswers }) =>
    set({
      activeStep: "review",
      digitalProfile,
      mandatoryData,
      surveyAnswers,
      isEditingFromReview: false,
    }),

  // Returning homeowner with saved mandatory details but no finished survey:
  // skip the mandatory form and land on the survey. Stays on "mandatory" as
  // the underlying step so the survey's back arrow has somewhere to return to.
  resumeAtSurvey: ({ digitalProfile, mandatoryData, surveyAnswers }) =>
    set({
      activeStep: "mandatory",
      digitalProfile,
      mandatoryData,
      surveyAnswers,
      isEditingFromReview: false,
    }),

  // Survey's back arrow bouncing back one step (not the review-edit case).
  backToMandatory: (mandatoryData) =>
    set((state) => ({
      activeStep: "mandatory",
      mandatoryData: mandatoryData ?? state.mandatoryData,
      isEditingFromReview: false,
    })),

  goToReview: ({ mandatoryData, surveyAnswers } = {}) =>
    set((state) => ({
      activeStep: "review",
      mandatoryData: mandatoryData ?? state.mandatoryData,
      surveyAnswers: surveyAnswers ?? state.surveyAnswers,
    })),

  editMandatory: () => set({ activeStep: "mandatory", isEditingFromReview: true }),

  // Submission succeeded — show the confirmation as its own sliding page
  // rather than a separate overlay stacked on top of Review.
  goToSuccess: () => set({ activeStep: "success" }),

  close: () =>
    set({
      activeStep: null,
      digitalProfile: null,
      mandatoryData: null,
      surveyAnswers: {},
      isEditingFromReview: false,
    }),
}));
